import { useState, useEffect } from "react"
import { Cpu, Play, TrendingUp, Activity, Zap, BarChart2, RefreshCw, Target, ArrowRight } from "lucide-react"
import Badge from "../components/common/Badge"
import { STRATEGY_TYPES } from "../utils/constants"
import api from "../services/api"
import { PageLoader } from "../components/common/LoadingSpinner"
import { useNavigate } from "react-router-dom"

const RISK_VARIANT = { Low: "green", Medium: "yellow", High: "red", Variable: "blue" }
const CAT_ICONS = { Trend: TrendingUp, "Mean Reversion": RefreshCw, Momentum: Activity, Breakout: Zap, Investment: BarChart2, Neutral: Target, Arbitrage: ArrowRight, Scalping: Activity, Custom: Cpu }
const STRATEGY_DETAILS = {
  ema_crossover:         { desc: "Trades when a fast EMA crosses a slow EMA. Best in trending markets.", params: ["Fast Period: 9","Slow Period: 21","Stop Loss: 2%","Take Profit: 4%"] },
  rsi_mean_reversion:    { desc: "Buys oversold conditions (RSI<30) and sells overbought (RSI>70).", params: ["RSI Period: 14","Oversold: 30","Overbought: 70"] },
  macd_momentum:         { desc: "MACD crossovers and histogram momentum for trade entries.", params: ["Fast: 12","Slow: 26","Signal: 9"] },
  bollinger_bands:       { desc: "Buys near lower band and sells near upper band in ranging markets.", params: ["Period: 20","Std Dev: 2.0","Stop: 1.5%"] },
  breakout:              { desc: "Enters when price breaks above/below a significant range with volume.", params: ["Lookback: 20","Volume: 1.5x","Stop: 3%"] },
  dca:                   { desc: "Accumulates at regular intervals regardless of price action.", params: ["Frequency: Daily","Amount: Fixed","Max Orders: 10"] },
  grid_trading:          { desc: "Places buy/sell orders at fixed price intervals within a range.", params: ["Grid Lines: 10","Range: ±5%","Per Grid: 1%"] },
  volatility_breakout:   { desc: "Trades breakouts triggered by abnormal volatility expansion.", params: ["ATR Period: 14","ATR Multiplier: 2.0","Hold: 4 bars"] },
  portfolio_rebalancing: { desc: "Rebalances weights back to target allocations when drift exceeds threshold.", params: ["Rebalance Threshold: 5%","Max Drift: 10%"] },
  arbitrage_monitor:     { desc: "Monitors price differences across exchanges for arb opportunities.", params: ["Min Spread: 0.3%","Exchanges: 2+"] },
  trend_following:       { desc: "Multi-timeframe trend confirmation before entering positions.", params: ["MTF1: 1h","MTF2: 4h","MTF3: 1d","Confirmation: 2/3"] },
  smart_scalping:        { desc: "High-frequency entries on micro pullbacks with tight risk controls.", params: ["Target: 0.3%","Stop: 0.15%","Max Daily: 20"] },
  custom:                { desc: "Build a completely custom strategy with your own parameters and logic.", params: [] },
}

function StrategyCard({ strategy, isSelected, onClick }) {
  const info = STRATEGY_TYPES.find(s => s.value === strategy.type) || {}
  const Icon = CAT_ICONS[info.category] || Cpu
  const details = STRATEGY_DETAILS[strategy.type] || {}
  return (
    <div onClick={onClick} className={`card cursor-pointer transition-all ${isSelected ? "border-brand-500/60 bg-brand-500/5" : "hover:border-surface-300"}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className={`p-2.5 rounded-xl shrink-0 ${isSelected ? "bg-brand-500/20" : "bg-surface-100"}`}>
          <Icon size={18} className={isSelected ? "text-brand-400" : "text-gray-400"} />
        </div>
        <div>
          <h3 className="font-semibold text-white text-sm">{strategy.name}</h3>
          <p className="text-xs text-gray-400 mt-0.5">{details.desc?.slice(0,60)}...</p>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        <Badge variant="blue">{info.category || "Strategy"}</Badge>
        <Badge variant={RISK_VARIANT[strategy.risk_level] || "gray"}>{strategy.risk_level} Risk</Badge>
        {strategy.is_active && <Badge variant="green">Active</Badge>}
      </div>
    </div>
  )
}

function StrategyDetail({ strategy }) {
  const info = STRATEGY_TYPES.find(s => s.value === strategy.type) || {}
  const details = STRATEGY_DETAILS[strategy.type] || {}
  const navigate = useNavigate()
  return (
    <div className="card space-y-5">
      <div className="flex items-start gap-3 pb-4 border-b border-surface-200">
        <div className="p-3 bg-brand-500/10 rounded-xl"><Cpu size={22} className="text-brand-400" /></div>
        <div>
          <h3 className="font-bold text-white text-lg">{strategy.name}</h3>
          <p className="text-sm text-gray-400 mt-1">{strategy.description || details.desc}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {[["Category", info.category||"—"],["Risk Level", strategy.risk_level||"—"],["Strategy Type", strategy.type],["Status", strategy.is_active?"Active":"Inactive"]].map(([k,v]) => (
          <div key={k} className="bg-surface-100 rounded-xl p-3">
            <p className="text-xs text-gray-400 mb-1">{k}</p>
            <p className="text-sm font-semibold text-white capitalize">{v}</p>
          </div>
        ))}
      </div>
      {details.params?.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-2">Default Parameters</p>
          <div className="space-y-2">
            {details.params.map(p => (
              <div key={p} className="flex items-center justify-between text-sm bg-surface-100 rounded-lg px-3 py-2">
                <span className="text-gray-400">{p.split(":")[0]}</span>
                <span className="text-white font-mono font-medium">{p.split(":")[1]?.trim()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {strategy.default_parameters && Object.keys(strategy.default_parameters).length > 0 && (
        <div>
          <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-2">Live Parameters</p>
          <div className="space-y-2">
            {Object.entries(strategy.default_parameters).map(([k,v]) => (
              <div key={k} className="flex items-center justify-between text-sm bg-surface-100 rounded-lg px-3 py-2">
                <span className="text-gray-400">{k}</span>
                <span className="text-white font-mono">{String(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex gap-3 pt-2">
        <button onClick={() => navigate("/backtesting")} className="btn-primary flex-1 flex items-center justify-center gap-2 text-sm"><Play size={14}/> Backtest</button>
        <button onClick={() => navigate("/bots")} className="btn-secondary flex-1 flex items-center justify-center gap-2 text-sm"><Cpu size={14}/> Deploy Bot</button>
      </div>
    </div>
  )
}

export default function Strategies() {
  const [strategies, setStrategies] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [catFilter, setCatFilter] = useState("All")

  useEffect(() => {
    Promise.all([api.get("/strategies/"), api.get("/strategies/registry")]).then(([s, registry]) => {
      let list = s.data || []
      // Merge registry strategies that aren't already in user strategies
      if (registry.data?.length) {
        const existingTypes = new Set(list.map(st => st.type))
        registry.data.forEach(r => {
          if (!existingTypes.has(r.type || r.strategy_type)) {
            list = [...list, { id: r.id || r.type, name: r.name, type: r.type || r.strategy_type, risk_level: r.risk || "Medium", is_active: false, description: r.description }]
          }
        })
      }
      // Fallback: show all known strategies as demos if nothing loaded
      if (list.length === 0) {
        list = STRATEGY_TYPES.filter(t => t.value !== "custom").map((t, i) => ({
          id: `demo-${t.value}`, name: t.label, type: t.value, risk_level: t.risk, is_active: false, description: STRATEGY_DETAILS[t.value]?.desc || ""
        }))
      }
      setStrategies(list)
      if (list.length > 0) setSelected(list[0])
    }).catch(() => {
      const fallback = STRATEGY_TYPES.filter(t => t.value !== "custom").map(t => ({
        id: `demo-${t.value}`, name: t.label, type: t.value, risk_level: t.risk, is_active: false, description: STRATEGY_DETAILS[t.value]?.desc || ""
      }))
      setStrategies(fallback)
      if (fallback.length > 0) setSelected(fallback[0])
    }).finally(() => setLoading(false))
  }, [])

  const cats = ["All", ...new Set(strategies.map(s => STRATEGY_TYPES.find(t => t.value === s.type)?.category).filter(Boolean))]
  const filtered = catFilter === "All" ? strategies : strategies.filter(s => STRATEGY_TYPES.find(t => t.value === s.type)?.category === catFilter)

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white">Strategies</h2>
        <p className="text-sm text-gray-400 mt-1">{strategies.length} strategies · select one to view details, backtest or deploy</p>
      </div>
      {cats.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {cats.map(c => (<button key={c} onClick={() => setCatFilter(c)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${catFilter === c ? "bg-brand-500 text-white" : "text-gray-400 hover:text-white bg-surface-100"}`}>{c}</button>))}
        </div>
      )}
      {loading ? <PageLoader /> : strategies.length === 0 ? (
        <div className="card text-center py-16"><Cpu size={40} className="mx-auto text-gray-600 mb-3"/><h3 className="font-semibold text-white mb-2">No strategies loaded</h3><p className="text-gray-400 text-sm">Strategies appear here once your account is configured</p></div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 content-start">
            {filtered.map(s => (<StrategyCard key={s.id} strategy={s} isSelected={selected?.id === s.id} onClick={() => setSelected(s)}/>))}
          </div>
          <div className="xl:col-span-1">{selected ? <StrategyDetail strategy={selected}/> : <div className="card text-center py-10 text-gray-500"><Cpu size={32} className="mx-auto mb-2"/><p className="text-sm">Select a strategy</p></div>}</div>
        </div>
      )}
    </div>
  )
}
